import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Logo } from '@/components/ui/Logo'

export function Privacy() {
  return (
    <div className="min-h-screen bg-black">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="transition-transform hover:scale-105">
            <Logo className="text-white" />
          </Link>
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span>Voltar</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="relative container mx-auto px-4 py-16 max-w-3xl">
        <div className="mb-12">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
            Legal
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
            Política de Privacidade
          </h1>
          <p className="text-sm text-gray-600">
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </p>
        </div>

        <div className="space-y-10 text-gray-400">
          <Section title="1. Informações Coletadas">
            <p>
              O zonisigma.app coleta apenas as informações necessárias para o funcionamento do serviço:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Código de integração fornecido pelo usuário',
                'Dados de trading sincronizados da conta Futura Broker',
                'Informações básicas do perfil (nome, email)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-600 mt-2" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="2. Uso das Informações">
            <p>Utilizamos suas informações para:</p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Exibir estatísticas e métricas de desempenho',
                'Gerar gráficos e relatórios personalizados',
                'Melhorar a experiência do usuário',
                'Manter a segurança da plataforma',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-600 mt-2" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="3. Armazenamento de Dados">
            <p>
              Seus dados são armazenados de forma segura em servidores protegidos. Implementamos
              medidas técnicas e organizacionais para proteger suas informações contra acesso não
              autorizado, alteração ou destruição.
            </p>
          </Section>

          <Section title="4. Compartilhamento de Dados">
            <p>
              Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros,
              exceto quando necessário para:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Cumprir obrigações legais',
                'Proteger nossos direitos e segurança',
                'Fornecer o serviço solicitado (integração com Futura Broker)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-600 mt-2" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="5. Cookies e Tecnologias">
            <p>
              Utilizamos armazenamento local (localStorage) para manter sua sessão ativa e
              preferências de uso. Não utilizamos cookies de rastreamento de terceiros.
            </p>
          </Section>

          <Section title="6. Seus Direitos">
            <p>Você tem direito a:</p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Acessar seus dados pessoais',
                'Solicitar correção de dados incorretos',
                'Solicitar exclusão de seus dados',
                'Revogar seu consentimento a qualquer momento',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-600 mt-2" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="7. Retenção de Dados">
            <p>
              Mantemos seus dados enquanto sua conta estiver ativa. Após a exclusão da conta,
              seus dados serão removidos em até 30 dias, exceto quando houver obrigação legal
              de retenção.
            </p>
          </Section>

          <Section title="8. Alterações nesta Política">
            <p>
              Podemos atualizar esta política periodicamente. Notificaremos sobre mudanças
              significativas através da plataforma.
            </p>
          </Section>

          <Section title="9. Contato">
            <p>
              Para questões sobre privacidade ou para exercer seus direitos, entre em contato
              através do email disponível na plataforma.
            </p>
          </Section>
        </div>
      </main>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </section>
  )
}
